import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError, API } from "@/lib/api";
import { PageHeader } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Gear, CheckCircle, ChatCircleDots } from "@phosphor-icons/react";
import { Video, Image as ImageIcon, Trash2, Plus, Upload, CheckCircle2 } from "lucide-react";

export default function SystemSettings() {
  const [health, setHealth] = useState(null);
  const [chatScript, setChatScript] = useState("");
  const [loadingChat, setLoadingChat] = useState(true);
  const [savingChat, setSavingChat] = useState(false);

  useEffect(() => { api.get("/health").then((r) => setHealth(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    api.get("/settings")
      .then((r) => setChatScript(r.data?.chat_widget_script || ""))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoadingChat(false));
  }, []);

  const saveChat = async () => {
    setSavingChat(true);
    try {
      const { data } = await api.put("/settings", { chat_widget_script: chatScript });
      setChatScript(data?.chat_widget_script || "");
      toast.success("Chat widget saved — it now shows on the landing page.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSavingChat(false); }
  };

  const rows = [
    ["Currency", "USD (single currency)"],
    ["Money representation", "Integer minor-units (no floats)"],
    ["Billing rounding", "Per-minute (configurable) — applied at CDR billing time"],
    ["Default platform price", "$10.00 / concurrency slot"],
    ["Auth", "Email + password · JWT (Bearer)"],
    ["Tenant isolation", "tenant_id query-guard on every scoped read/write"],
  ];

  return (
    <div>
      <PageHeader title="System Settings" subtitle="Platform defaults and service health" />
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="rounded-xl border border-border bg-card card-shadow p-6" data-testid="settings-defaults">
          <div className="flex items-center gap-2 mb-4"><Gear className="w-5 h-5 text-primary" weight="duotone" /><h3 className="font-semibold">Platform Defaults</h3></div>
          <dl className="divide-y">
            {rows.map(([k, v]) => (
              <div key={k} className="flex items-start justify-between py-3 gap-6">
                <dt className="text-sm text-muted-foreground">{k}</dt>
                <dd className="text-sm font-medium text-right">{v}</dd>
              </div>
            ))}
          </dl>
        </div>
        <div className="rounded-xl border border-border bg-card card-shadow p-6" data-testid="settings-health">
          <h3 className="font-semibold mb-4">Service Health</h3>
          <div className="flex items-center gap-3 rounded-lg border border-border p-4">
            <CheckCircle className={`w-8 h-8 ${health?.status === "ok" ? "text-[hsl(var(--success))]" : "text-muted-foreground"}`} weight="fill" />
            <div>
              <p className="font-medium capitalize">{health?.status || "checking…"}</p>
              <p className="text-sm text-muted-foreground">Database: {health?.db || "—"} · {health?.service}</p>
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-4">Telephony stack (OpenSIPS / FreeSWITCH / VAD / faster-whisper) is scaffolded and wired in a later step.</p>
        </div>
      </div>

      {/* Chat widget */}
      <div className="rounded-xl border border-border bg-card card-shadow p-6 mt-6" data-testid="settings-chat-widget">
        <div className="flex items-center gap-2 mb-1"><ChatCircleDots className="w-5 h-5 text-primary" weight="duotone" /><h3 className="font-semibold">Landing Page Chat Widget</h3></div>
        <p className="text-sm text-muted-foreground mb-4">
          Paste a chat widget embed snippet (e.g. from <span className="font-medium">tawk.to</span>, Crisp, Intercom). It is
          injected <span className="font-medium">only on the public landing page</span> — never inside the app. Leave empty to remove the widget.
        </p>
        <Textarea
          data-testid="chat-widget-script"
          value={chatScript}
          onChange={(e) => setChatScript(e.target.value)}
          disabled={loadingChat || savingChat}
          placeholder={loadingChat ? "Loading…" : '<!--Start of Tawk.to Script-->\n<script type="text/javascript">\n  var Tawk_API=Tawk_API||{};\n  (function(){ var s1=document.createElement("script"); s1.async=true; s1.src="https://embed.tawk.to/XXXXXXXX/default"; s1.charset="UTF-8"; s1.setAttribute("crossorigin","*"); document.body.appendChild(s1); })();\n</script>\n<!--End of Tawk.to Script-->'}
          className="font-mono text-xs min-h-[180px]"
        />
        <div className="flex items-center justify-between mt-3">
          <p className="text-xs text-muted-foreground">Raw HTML/JS is executed on the landing page. Only paste snippets from providers you trust.</p>
          <Button onClick={saveChat} disabled={loadingChat || savingChat} data-testid="chat-widget-save" className="rounded-lg">
            {savingChat ? "Saving…" : "Save widget"}
          </Button>
        </div>
      </div>

      <IntroVideos />
    </div>
  );
}

function IntroVideos() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newTitle, setNewTitle] = useState("");
  const [adding, setAdding] = useState(false);

  const load = () =>
    api.get("/settings/intro-videos")
      .then((r) => setList(r.data?.intro_videos || []))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!newTitle.trim()) return;
    setAdding(true);
    try {
      const fd = new FormData();
      fd.append("title", newTitle.trim());
      await api.post("/settings/intro-videos", fd);
      setNewTitle("");
      toast.success("Video added.");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setAdding(false); }
  };

  return (
    <div className="rounded-xl border border-border bg-card card-shadow p-6 mt-6" data-testid="settings-intro-videos">
      <div className="flex items-center gap-2 mb-1"><Video className="w-5 h-5 text-primary" /><h3 className="font-semibold">Landing Page Intro Videos</h3></div>
      <p className="text-sm text-muted-foreground mb-4">
        Upload intro videos and their cover images. Covers appear on the public landing page (right after the campaign-flow section);
        visitors click play to watch the video in a popup. Video max 150MB (mp4/mov/webm), cover max 10MB (png/jpg/webp).
      </p>

      <div className="flex items-center gap-2 mb-5">
        <Input
          data-testid="intro-video-new-title"
          value={newTitle}
          onChange={(e) => setNewTitle(e.target.value)}
          placeholder="New video title…"
          className="max-w-sm"
        />
        <Button onClick={add} disabled={adding || !newTitle.trim()} data-testid="intro-video-add" className="rounded-lg">
          <Plus className="w-4 h-4 mr-1" /> {adding ? "Adding…" : "Add video"}
        </Button>
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : list.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="intro-video-empty">No intro videos yet. Add one above.</p>
      ) : (
        <div className="space-y-4">
          {list.map((it) => <IntroVideoRow key={it.id} item={it} onChanged={load} />)}
        </div>
      )}
    </div>
  );
}

function IntroVideoRow({ item, onChanged }) {
  const [title, setTitle] = useState(item.title || "");
  const [videoFile, setVideoFile] = useState(null);
  const [coverFile, setCoverFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const coverUrl = item.has_cover
    ? `${API}/settings/intro-videos/${item.id}/cover?v=${encodeURIComponent(item.updated_at || "")}`
    : null;

  const save = async () => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("title", title);
      if (videoFile) fd.append("video", videoFile);
      if (coverFile) fd.append("cover", coverFile);
      await api.put(`/settings/intro-videos/${item.id}`, fd);
      toast.success("Saved.");
      setVideoFile(null); setCoverFile(null);
      onChanged();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Remove "${item.title}"?`)) return;
    setBusy(true);
    try { await api.delete(`/settings/intro-videos/${item.id}`); toast.success("Removed."); onChanged(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-lg border border-border p-4 flex flex-col md:flex-row gap-4" data-testid={`intro-video-row-${item.id}`}>
      <div className="w-full md:w-40 shrink-0">
        <div className="aspect-video rounded-md overflow-hidden bg-muted grid place-items-center">
          {coverUrl
            ? <img src={coverUrl} alt={item.title} className="w-full h-full object-cover" data-testid={`intro-video-cover-${item.id}`} />
            : <ImageIcon className="w-6 h-6 text-muted-foreground" />}
        </div>
      </div>
      <div className="flex-1 space-y-3">
        <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title" data-testid={`intro-video-title-${item.id}`} />
        <div className="grid sm:grid-cols-2 gap-3">
          <label className="text-xs text-muted-foreground">
            <span className="flex items-center gap-1 mb-1"><Upload className="w-3.5 h-3.5" /> Video {item.has_video && <span className="text-[hsl(var(--success))] inline-flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> uploaded</span>}</span>
            <input type="file" accept="video/mp4,video/quicktime,video/webm,.mp4,.mov,.m4v,.webm"
              onChange={(e) => setVideoFile(e.target.files?.[0] || null)}
              data-testid={`intro-video-file-${item.id}`} className="block w-full text-xs" />
          </label>
          <label className="text-xs text-muted-foreground">
            <span className="flex items-center gap-1 mb-1"><ImageIcon className="w-3.5 h-3.5" /> Cover image {item.has_cover && <span className="text-[hsl(var(--success))] inline-flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> uploaded</span>}</span>
            <input type="file" accept="image/png,image/jpeg,image/webp,image/gif,.png,.jpg,.jpeg,.webp,.gif"
              onChange={(e) => setCoverFile(e.target.files?.[0] || null)}
              data-testid={`intro-cover-file-${item.id}`} className="block w-full text-xs" />
          </label>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={save} disabled={busy} data-testid={`intro-video-save-${item.id}`} className="rounded-lg">
            {busy ? "Saving…" : "Save"}
          </Button>
          <Button onClick={remove} disabled={busy} variant="outline" data-testid={`intro-video-remove-${item.id}`} className="rounded-lg text-destructive">
            <Trash2 className="w-4 h-4 mr-1" /> Remove
          </Button>
        </div>
      </div>
    </div>
  );
}
